import { motion } from 'framer-motion'
import { LogOut } from 'lucide-react'

import type { User } from 'firebase/auth'

import { useAuth } from '../../context/auth/useAuth'
import { useI18n } from '../../context/i18n/useI18n'

import { useNavigate } from '@tanstack/react-router'
import { getInitials } from '../../utils/getInitials'

interface UserMenuProps {
  user: User
}

export const UserMenu = ({ user }: UserMenuProps) => {
  const navigate = useNavigate()

  const { logout } = useAuth()
  const { t } = useI18n()
  const handleLogout = async () => {
    try {
      await logout()
      navigate({ to: '/login' })
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const userEmail = user.email || ''
  const displayName = user.displayName || user.email?.split('@')[0] || 'User'
  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="absolute right-0 mt-3 w-72 sm:w-80 bg-surface-secondary rounded-2xl shadow-2xl shadow-black/20 backdrop-blur-xl z-50"
    >
      <div className="p-2 sm:p-3">
        <div className="w-full flex items-center space-x-3 sm:space-x-4 px-3 sm:px-4 py-3 sm:py-4 text-left text-sm hover:bg-interactive-secondary-hover rounded-xl transition-all duration-200">
          <div className="flex items-center space-x-3 sm:space-x-4 w-full">
            <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-linear-to-br from-blue-600 to-blue-500 rounded-2xl flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0">
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={displayName}
                  className="w-full h-full rounded-xl object-cover"
                  width={48}
                  height={48}
                />
              ) : (
                <span className="text-xs sm:text-sm">{getInitials(displayName)}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-primary-shell truncate">{displayName}</p>
              <p className="text-[10px] sm:text-xs text-text-se condary truncate mt-1">{userEmail}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-2 sm:p-3">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 sm:space-x-4 px-3 sm:px-4 py-3 sm:py-4 text-left text-sm hover:bg-error-500/10 rounded-xl transition-all duration-200 cursor-pointer"
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-error-500/20 rounded-xl flex items-center justify-center transition-colors shadow-sm shrink-0">
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-error-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-error-500 font-semibold text-xs sm:text-sm">{t('userMenu.logout')}</p>
            <p className="text-[10px] sm:text-xs text-text-secondary mt-1">{t('userMenu.logoutDescription')}</p>
          </div>
        </button>
      </div>
    </motion.div>
  )
}
